/* eslint-disable indent */
import React, { useEffect, useMemo, useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import classNames from 'classnames';
import { CompletionStatus } from '../../../enums/completion-status';
import { PagePath } from '../../../navigation/navigation.enums';
import { AssessmentType } from '../../../enums/assessment-type';
import { Button, PageHeader } from '@keplerco/core';
import styles from './assessment.module.css';
import analysisStyles from '../analysis.module.css';
import { AssessmentResponse } from '../../../overmind/actions';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import AssessmentSvg from './assessment.svg';
import { useAnalysisBasePagePath } from '../use-analysis-base-page-path';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { FetchType } from '../../../enums';
import { getSubdomain } from '../../../library/helpers/get-subdomain';

function generateDefaultDescription(assessmentType: AssessmentType | undefined): string {
  if (assessmentType === undefined) return 'No description available';

  const subdomain = getSubdomain();

  if (assessmentType === AssessmentType.PeerEndorsement) {
    return 'Rate how consistently these skills are shown in daily work and its impact. This helps build a fair view of strengths and growth areas.';
  }

  if (assessmentType === AssessmentType.Questionnaire) {
    if (subdomain.includes('roche')) {
      return 'Your analysis will give us the insights we need to build your personalised development plan.';
    }

    return 'Read each question carefully and choose the answer that best describes how you may respond in the situation.';
  }

  if (assessmentType === AssessmentType.FieldsOfPractice) {
    if (subdomain.includes('lpi')) {
      return 'Choose your role, and let us know where you are heading with your career. Then rate yourself on your skills.';
    }

    return 'Choose a career pathway and let us know where you are heading with your career.';
  }

  return 'No description available';
}

export function AssessmentPage(): JSX.Element {
  const keplerNavigate = useKeplerNavigate();
  const basePagePath = useAnalysisBasePagePath();

  const actions = useAppActions();
  const { forcedAssessments, activeAssessmentConfig } = useAppState();

  const path: PagePath.analysisPendingAssessmentsAssessment | PagePath.analysisAssessment = useMemo(() => (!!forcedAssessments.length ? PagePath.analysisPendingAssessmentsAssessment : PagePath.analysisAssessment), [forcedAssessments.length]);

  const [assessmentDetails, setAssessmentDetails] = useState<AssessmentResponse | undefined>();

  useEffect(() => {
    async function getAssessmentDetails() {
      if (!activeAssessmentConfig) return;
      actions.startLoader({ path, type: FetchType.PageFetching });
      const response = await actions.getAssessment(activeAssessmentConfig.assessmentSlug);
      setAssessmentDetails(response);
      actions.stopLoader(path);
    }

    getAssessmentDetails();
  }, [activeAssessmentConfig?.assessmentSlug]);

  return (
    <PageLoaderLayer path={path}>
      <div className={classNames('pageWrapper wrapper', analysisStyles.interstitialPage)}>
        <PageHeader
          breadcrumbs={
             forcedAssessments.length > 1
              ? [
                  {
                    name: 'Pending assessments',
                    url: PagePath.analysisBase + PagePath.analysisPendingAssessments,
                  },
                  {
                    name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                    url: location.pathname,
                  },
                ]
              : [
                  {
                    name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                    url: location.pathname,
                  },
                ]
          }
        />

        <section className={analysisStyles.interstitialSection}>
          <figure className={analysisStyles.interstitialFigure}>
            <div className={analysisStyles.interstitialSVG}>
              <AssessmentSvg />
            </div>
          </figure>

          <div className={analysisStyles.interstitialContent}>
            <div style={{ marginBottom: 24 }}>
              <h1>{activeAssessmentConfig?.assessmentName}</h1>
              <p style={{ marginTop: 4, color: 'var(--text_1)' }}>{assessmentDetails?.description || generateDefaultDescription(activeAssessmentConfig?.assessmentType)}</p>
            </div>

            <Button
              type="button"
              onClick={() => {
                if (activeAssessmentConfig?.assessmentType === AssessmentType.PeerEndorsement) {
                  const selfRatingRequired = activeAssessmentConfig?.selfReviewCompletionStatus !== CompletionStatus.NoActionRequired;
                  keplerNavigate(basePagePath + (selfRatingRequired ? PagePath.analysisFrequencyOfUse : PagePath.analysisRateYourPeersSkills));
                  return;
                }

                if (activeAssessmentConfig?.assessmentType === AssessmentType.Questionnaire) {
                  keplerNavigate(basePagePath + PagePath.analysisQuestionnaires);
                  return;
                }

                if (activeAssessmentConfig?.assessmentType === AssessmentType.FieldsOfPractice) {
                  keplerNavigate(basePagePath + PagePath.analysisChooseYourCareerPaths);
                  return;
                }

                keplerNavigate(basePagePath);
              }}
              className={styles.startButton}
            >
              Start Assessment
            </Button>
          </div>
        </section>
      </div>
    </PageLoaderLayer>
  );
}
